import React, { useCallback, useEffect, useState } from "react";
import { Monitor, Smartphone, LogOut, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SectionShell, SCard, relativeFrTime } from "./SettingsUI";
import api from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/i18n";

export const SectionSessions = ({ reload }) => {
  const { tf } = useLang();
  const [sessions, setSessions] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await api.get("/account/sessions");
      setSessions(r.data.sessions);
    } catch { toast.error(tf("Impossible de charger les sessions")); }
  }, [tf]);
  useEffect(() => { load(); }, [load]);

  const revoke = async (sid) => {
    try {
      await api.delete(`/account/sessions/${sid}`);
      toast.success(tf("Appareil déconnecté"));
      load(); reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); }
  };
  const revokeOthers = async () => {
    try {
      const r = await api.post("/account/sessions/revoke-others");
      toast.success(`${r.data.revoked} ${tf("appareil(s) déconnecté(s)")}`);
      load(); reload?.();
    } catch { toast.error(tf("Erreur")); }
  };

  return (
    <SectionShell title={tf("Sessions & appareils")} desc={tf("Consultez et gérez les appareils connectés à votre compte.")} testid="section-sessions">
      <SCard testid="sessions-card"
        right={sessions?.length > 1 && (
          <Button variant="outline" size="sm" onClick={revokeOthers} data-testid="revoke-others-btn">
            {tf("Déconnecter tous les autres")}
          </Button>
        )}>
        {!sessions ? (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : sessions.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">{tf("Aucune session active.")}</p>
        ) : (
          <div className="space-y-2">
            {sessions.map((s) => {
              const Icon = s.device_type === "mobile" ? Smartphone : Monitor;
              return (
                <div key={s.sid} className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3.5" data-testid={`session-row-${s.sid}`}>
                  <div className="h-9 w-9 rounded-lg bg-white/[0.04] border border-white/[0.07] flex items-center justify-center shrink-0">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium flex items-center gap-2 flex-wrap">
                      {[s.device, s.browser].filter(Boolean).join(" · ") || tf("Appareil")}
                      {s.current && <span className="text-[11px] text-green-500 rounded-full border border-green-500/25 bg-green-500/[0.06] px-2 py-0.5">{tf("Session actuelle")}</span>}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {[s.ip, s.current ? tf("maintenant") : relativeFrTime(s.last_seen_at)].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  {!s.current && (
                    <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-red-400 shrink-0" onClick={() => revoke(s.sid)} data-testid={`session-revoke-${s.sid}`}>
                      <LogOut className="h-3.5 w-3.5 mr-1.5" /> {tf("Déconnecter")}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <p className="text-[11px] text-muted-foreground mt-4">{tf("Les informations d'appareil et de navigateur sont détectées à la connexion.")}</p>
      </SCard>
    </SectionShell>
  );
};
